/**
 * The Admin's incentive zones (W10-MARKET-SIGNALS, D488, ADR-0101; doc 17 §3.2) — framework-neutral view-models.
 *
 *  - **Operações › Zonas de incentivo** (`incentive_zones`): the market's zones, each with its in-force version and any version
 *    waiting for a decision. A market that has drawn none still has ONE implicit MARKET-WIDE zone: a point no drawn zone
 *    contains belongs to it, so the zones partition the market. The editor turns what the operator types (the points of a
 *    polygon, or a centre and a radius) into a proposal — a name, a priority, the window in the market's zone, the active
 *    switch. ANOTHER operator approves it; the console never offers the maker his own approval (the server refuses it too).
 *
 * The console only presents: the server authorizes every call at the market asked for. Neutral: no country, zone name,
 * currency or language is named here.
 */
import type { IncentiveZoneDefinitionView, IncentiveZoneProposalInput, IncentiveZoneRecordView, RiskAreaGeometryView } from '@vello/api-client';
import { grantsAllow, type KnownGrants } from './console-access.js';
import { zonedLocalToUtc } from './market-time.js';
import { roundPoint } from './risk-alerts.js';

export function incentiveZoneAuthority(grants: KnownGrants): { readonly mayView: boolean; readonly mayManage: boolean } {
  if (grants === undefined) return { mayView: false, mayManage: false };
  return {
    mayView: grantsAllow(grants, { permission: 'incentive_zone:view', target: 'GEO' }),
    mayManage: grantsAllow(grants, { permission: 'incentive_zone:manage', target: 'GEO' }),
  };
}

export interface IncentiveZoneRow {
  readonly zoneId: string;
  /** The newest version of any status (what the row is named by). */
  readonly latest: IncentiveZoneRecordView;
  /** The version in force: the newest APPROVED one in effect at `at`. */
  readonly approved?: IncentiveZoneRecordView;
  /** The version waiting for a decision, if any (at most one per zone). */
  readonly pending?: IncentiveZoneRecordView;
  readonly versions: number;
}

/** Group the versions of a market by zone, newest first; each carries its approved (in effect at `at`) and its pending version. */
export function groupIncentiveZones(records: readonly IncentiveZoneRecordView[], at?: string): readonly IncentiveZoneRow[] {
  const byZone = new Map<string, IncentiveZoneRecordView[]>();
  for (const r of records) byZone.set(r.definition.zoneId, [...(byZone.get(r.definition.zoneId) ?? []), r]);
  const rows: IncentiveZoneRow[] = [];
  for (const [zoneId, versions] of byZone) {
    const sorted = [...versions].sort((a, b) => b.definition.version - a.definition.version);
    const inEffect = at === undefined ? undefined : sorted.find((v) => v.status === 'APPROVED' && Date.parse(v.definition.effectiveFrom) <= Date.parse(at));
    const approved = inEffect ?? sorted.find((v) => v.status === 'APPROVED');
    const pending = sorted.find((v) => v.status === 'PROPOSED');
    rows.push({ zoneId, latest: sorted[0] as IncentiveZoneRecordView, ...(approved !== undefined ? { approved } : {}), ...(pending !== undefined ? { pending } : {}), versions: sorted.length });
  }
  return rows.sort((a, b) => b.latest.proposedAt.localeCompare(a.latest.proposedAt) || a.zoneId.localeCompare(b.zoneId));
}

/** Whether the zone counts signals now: it has an approved version in effect, that version is active, and its window is open at `at`. */
export function zoneInForce(row: IncentiveZoneRow, at: string): boolean {
  const d = row.approved?.definition;
  if (d === undefined || !d.active) return false;
  const t = Date.parse(at);
  return Date.parse(d.effectiveFrom) <= t && (d.effectiveTo === undefined || t < Date.parse(d.effectiveTo));
}

export type ZoneTone = 'neutral' | 'success' | 'warning' | 'danger';
export const zoneStatusTone = (status: IncentiveZoneRecordView['status']): ZoneTone => (status === 'APPROVED' ? 'success' : status === 'PROPOSED' ? 'warning' : 'danger');

/** Why the signed-in operator cannot decide a version (undefined -> may decide). */
export function zoneDecideBlock(record: IncentiveZoneRecordView, operatorId: string | undefined, mayManage: boolean, selfApproval = false): 'NO_PERMISSION' | 'NOT_PENDING' | 'OWN_PROPOSAL' | undefined {
  if (!mayManage) return 'NO_PERMISSION';
  if (record.status !== 'PROPOSED') return 'NOT_PENDING';
  if (!selfApproval && operatorId !== undefined && record.proposedBy === operatorId) return 'OWN_PROPOSAL';
  return undefined;
}

// ---- the editor ------------------------------------------------------------------------------------------------------

export interface ZoneDraft {
  /** Set when the draft edits an existing zone (a new version under the same id). */
  readonly zoneId?: string;
  readonly name: string;
  readonly shape: 'polygon' | 'circle';
  readonly ring: readonly { readonly latitude: number; readonly longitude: number }[];
  readonly center?: { readonly latitude: number; readonly longitude: number };
  readonly radiusMeters: string;
  readonly priority: string;
  /** The market's local wall clock (`YYYY-MM-DDTHH:mm`). */
  readonly from: string;
  readonly to: string;
  readonly active: boolean;
}

export const EMPTY_ZONE_DRAFT: ZoneDraft = { name: '', shape: 'polygon', ring: [], radiusMeters: '', priority: '0', from: '', to: '', active: true };

export type ZoneDraftIssue = 'NAME_REQUIRED' | 'TOO_FEW_POINTS' | 'CENTER_REQUIRED' | 'RADIUS_INVALID' | 'PRIORITY_INVALID' | 'FROM_REQUIRED' | 'WINDOW_INVALID';

/** The proposal the server takes, with the market's local wall-clock entries converted to UTC. The first issue stops it. */
export function zoneDraftToProposal(draft: ZoneDraft, timeZone: string | undefined): { readonly ok: true; readonly input: IncentiveZoneProposalInput } | { readonly ok: false; readonly issue: ZoneDraftIssue } {
  const name = draft.name.trim();
  if (name === '') return { ok: false, issue: 'NAME_REQUIRED' };
  let geometry: RiskAreaGeometryView;
  if (draft.shape === 'polygon') {
    if (draft.ring.length < 3) return { ok: false, issue: 'TOO_FEW_POINTS' };
    geometry = { kind: 'polygon', ring: draft.ring.map(roundPoint) };
  } else {
    if (draft.center === undefined) return { ok: false, issue: 'CENTER_REQUIRED' };
    const radius = Number(draft.radiusMeters);
    if (!Number.isFinite(radius) || radius < 50 || radius > 100_000) return { ok: false, issue: 'RADIUS_INVALID' };
    geometry = { kind: 'circle', center: roundPoint(draft.center), radiusMeters: Math.round(radius) };
  }
  const priority = Number(draft.priority);
  if (draft.priority.trim() === '' || !Number.isInteger(priority) || priority < 0 || priority > 1000) return { ok: false, issue: 'PRIORITY_INVALID' };
  if (draft.from.trim() === '') return { ok: false, issue: 'FROM_REQUIRED' };
  const tz = timeZone ?? 'UTC';
  const effectiveFrom = zonedLocalToUtc(draft.from, tz);
  const effectiveTo = draft.to.trim() === '' ? undefined : zonedLocalToUtc(draft.to, tz);
  if (effectiveFrom === undefined || (draft.to.trim() !== '' && effectiveTo === undefined)) return { ok: false, issue: 'WINDOW_INVALID' };
  if (effectiveTo !== undefined && Date.parse(effectiveTo) <= Date.parse(effectiveFrom)) return { ok: false, issue: 'WINDOW_INVALID' };
  return {
    ok: true,
    input: { ...(draft.zoneId !== undefined ? { zoneId: draft.zoneId } : {}), name, geometry, priority, effectiveFrom, ...(effectiveTo !== undefined ? { effectiveTo } : {}), active: draft.active },
  };
}

/** A draft that edits an existing version (its shape, window and switch); the new version is proposed under the same zone. */
export function zoneDraftFromDefinition(d: IncentiveZoneDefinitionView, toLocal: (utc: string) => string | undefined): ZoneDraft {
  const g = d.geometry as RiskAreaGeometryView;
  return {
    zoneId: d.zoneId,
    name: d.name,
    shape: g.kind === 'circle' ? 'circle' : 'polygon',
    ring: g.kind === 'polygon' ? g.ring : [],
    ...(g.kind === 'circle' ? { center: g.center } : {}),
    radiusMeters: g.kind === 'circle' ? String(g.radiusMeters) : '',
    priority: String(d.priority),
    from: toLocal(d.effectiveFrom) ?? '',
    to: d.effectiveTo !== undefined ? toLocal(d.effectiveTo) ?? '' : '',
    active: d.active,
  };
}

